<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../../Button.svelte';
  import PaymentReportService from '../../../../lib/api/employer/payment-report';
  import toast from 'svelte-french-toast';

  // Combined payment data
  interface CharityDonators {
    charity: {
      uuid: string;
      name: string;
    };
    donators: Array<{
      uuid: string;
      name: string;
      hours: number;
      amount: number;
      employer_top_up?: number;
      contribution?: number;
    }>;
    totalAmount: number;
    totalHours: number;
    totalContribution?: number;
    payment_received?: number;
    l2g_fee_percentage?: number;
    l2g_fee_amount?: number;
    net_payment?: number;
    payment_reference?: string;
    transaction_id?: string;
    transaction_date?: string;
    status?: string;
  }

  export let invoiceId: string = '';

  let invoiceData = {
    invoice_id: '',
    invoice_date: '',
    paid_at: '',
    totalAmount: 0,
    totalDonations: 0,
    totalHours: 0,
    totalContribution: 0,
    totalCharities: 0,
    charities: [] as CharityDonators[]
  };

  // State
  let isLoading = true;
  let error = '';
  let isDownloading = false;
  let expandedCharities: Record<string, boolean> = {};

  function toggleCharity(charityUuid: string) {
    const currentState = expandedCharities[charityUuid] || false;
    expandedCharities = {
      ...expandedCharities,
      [charityUuid]: !currentState
    };
  }

  function isCharityExpanded(charityUuid: string): boolean {
    return expandedCharities[charityUuid] === true;
  }

  async function loadInvoiceDetails() {
    try {
      isLoading = true;
      error = '';
      
      const response = await PaymentReportService.getPaidInvoiceDetails(invoiceId);
      
      console.log('API Response:', response);
      
      if (response.success && response.data) {
        const data = response.data;
        
        console.log('Invoice data received:', data);
        console.log('Charities data:', data.charities);
        
        invoiceData = {
          invoice_id: data.invoice_id || '',
          invoice_date: data.invoice_date || '',
          paid_at: data.paid_at || '',
          totalAmount: data.totalAmount || 0,
          totalDonations: data.totalDonations || 0,
          totalHours: data.totalHours || 0,
            totalContribution: data.totalContribution || 0,
            totalCharities: data.totalCharities || 0,
            charities: (data.charities || []).map((charityGroup: any) => {
              console.log('Processing charity group:', charityGroup);
              console.log('Charity totalContribution:', charityGroup.totalContribution);
              return {
                charity: charityGroup.charity,
                donators: charityGroup.donators || [],
                totalAmount: charityGroup.totalAmount || 0,
                totalHours: charityGroup.totalHours || 0,
                totalContribution: charityGroup.totalContribution || 0,
                payment_received: charityGroup.payment_received,
                l2g_fee_percentage: charityGroup.l2g_fee_percentage,
                l2g_fee_amount: charityGroup.l2g_fee_amount,
                net_payment: charityGroup.net_payment,
                payment_reference: charityGroup.payment_reference,
                transaction_id: charityGroup.transaction_id,
                transaction_date: charityGroup.transaction_date,
                status: charityGroup.status
              };
            })
        };
        
        console.log('Final invoiceData:', invoiceData);
      } else {
        error = response.message || 'Failed to load invoice details';
        toast.error(error);
      }
    } catch (err: any) {
      error = 'Failed to load invoice details';
      toast.error(error);
      console.error('Error loading invoice details:', err);
    } finally {
      isLoading = false;
    }
  }

  function formatCurrency(amount: number | string): string {
    return `$${parseFloat(String(amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  async function handleDownloadInvoice() {
    if (!invoiceId || isDownloading) return;
    try {
      isDownloading = true;
      await PaymentReportService.downloadPaidInvoice(invoiceId);
      toast.success('Invoice downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading invoice:', err);
      const msg = err.response?.data?.message ?? (typeof err.response?.data === 'string' ? err.response?.data : null);
      toast.error(msg || 'Failed to download invoice');
    } finally {
      isDownloading = false;
    }
  }

  onMount(() => {
    console.log('=== PAID INVOICE DETAIL COMPONENT MOUNTED ===');
    console.log('Component mounted, invoiceId:', invoiceId);
    console.log('Component file updated:', new Date().toISOString());
    
    if (invoiceId) {
      console.log('Loading invoice details for ID:', invoiceId);
      loadInvoiceDetails();
    } else {
      console.error('ERROR: No invoiceId provided!');
      error = 'Invoice ID is required';
      isLoading = false;
    }
  });
</script>

<div class="paid-invoice-page">
  <div class="page-actions">
    <button class="back-link" on:click={() => {
      console.log('Back button clicked, navigating to payment history');
      push('/employer/payment-history');
    }}>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5"></path>
        <path d="M12 19l-7-7 7-7"></path>
      </svg>
      <span>Back</span>
    </button>
    {#if !isLoading && !error && invoiceData.invoice_id}
      <button
        class="download-button"
        on:click={handleDownloadInvoice}
        disabled={isDownloading}
        type="button"
      >
        {#if isDownloading}
          <span class="download-spinner" aria-hidden="true"></span>
          <span>Downloading…</span>
        {:else}
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Download invoice</span>
        {/if}
      </button>
    {/if}
  </div>

  <div class="invoice-section">
    {#if error}
      <div class="error-message">
        <p>{error}</p>
        <Button variant="secondary" on:click={loadInvoiceDetails}>Try Again</Button>
      </div>
    {:else}
      {#if isLoading}
        <div class="loading-message">Loading invoice details...</div>
      {:else}
        <div class="invoice-card">
          <div class="invoice-header">
            <div class="invoice-info">
              <h2 class="invoice-title">Invoice #{invoiceData.invoice_id}</h2>
              <div class="invoice-meta">
                <span class="paid-badge">With L2G</span>
                <span class="separator">•</span>
                <span>Paid on {formatDate(invoiceData.paid_at)}</span>
              </div>
              <div class="invoice-summary">
                <span>{invoiceData.totalDonations} donation{invoiceData.totalDonations !== 1 ? 's' : ''}</span>
                <span class="separator">•</span>
                <span>{invoiceData.totalHours} hours</span>
                <span class="separator">•</span>
                <span>{invoiceData.totalCharities} charities</span>
                <span class="separator">•</span>
                <span class="total-amount">Total: {formatCurrency(invoiceData.totalAmount)}</span>
              </div>
              <div class="contribution-summary-top">
                <span class="contribution-label">Total Contribution (Employer):</span>
                <span class="contribution-value">{formatCurrency(invoiceData.totalContribution)}</span>
                <span class="contribution-note">(included in total above)</span>
              </div>
            </div>
          </div>

          <!-- Donators grouped by charity -->
          <div class="donators-section">
            {#each invoiceData.charities as charityGroup (charityGroup.charity.uuid)}
              <div class="charity-group">
                <button 
                  class="charity-header-button"
                  on:click|stopPropagation={() => {
                    toggleCharity(charityGroup.charity.uuid);
                  }}
                  type="button"
                  aria-expanded={expandedCharities[charityGroup.charity.uuid] || false}
                >
                  <div class="charity-header">
                    <div class="charity-header-content">
                      <div class="charity-name-row">
                        <h3 class="charity-name">{charityGroup.charity.name}</h3>
                        {#if charityGroup.status}
                        <span class="charity-status-badge status-{charityGroup.status.toLowerCase()}">
                          {charityGroup.status}
                        </span>
                        {/if}
                      </div>
                      <div class="charity-summary">
                        <span>{charityGroup.donators.length} donation{charityGroup.donators.length !== 1 ? 's' : ''}</span>
                        <span class="separator">•</span>
                        <span>{charityGroup.totalHours} hours</span>
                        <span class="separator">•</span>
                        <span class="charity-total">Payment Received: {formatCurrency(charityGroup.payment_received || charityGroup.totalAmount)}</span>
                        {#if charityGroup.l2g_fee_amount}
                        <span class="separator">•</span>
                        <span class="l2g-fee">Transaction Fee: {formatCurrency(charityGroup.l2g_fee_amount)}</span>
                        <span class="separator">•</span>
                        <span class="net-payment">Net: {formatCurrency(charityGroup.net_payment || ((charityGroup.payment_received || charityGroup.totalAmount) - (charityGroup.l2g_fee_amount || 0)))}</span>
                        {/if}
                        {#if charityGroup.payment_reference}
                        <span class="separator">•</span>
                        <span class="payment-ref">Ref: {charityGroup.payment_reference}</span>
                        {/if}
                      </div>
                    </div>
                    <svg 
                      class="expand-icon" 
                      class:expanded={expandedCharities[charityGroup.charity.uuid]}
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      stroke-width="2" 
                      stroke-linecap="round" 
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </button>
                {#if expandedCharities[charityGroup.charity.uuid]}
                  <div class="donators-list">
                    {#if charityGroup.payment_received || charityGroup.l2g_fee_amount}
                    <div class="payment-summary-card">
                      <h4 class="summary-title">Payment Summary</h4>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Payment Received</span>
                          <span class="summary-value">{formatCurrency(charityGroup.payment_received || charityGroup.totalAmount)}</span>
                        </div>
                        {#if charityGroup.l2g_fee_percentage}
                        <div class="summary-item">
                          <span class="summary-label">Transaction Fee</span>
                          <span class="summary-value">{charityGroup.l2g_fee_percentage}% ({formatCurrency(charityGroup.l2g_fee_amount || 0)})</span>
                        </div>
                        {/if}
                        {#if charityGroup.net_payment}
                        <div class="summary-item">
                          <span class="summary-label">Net Payment to Charity</span>
                          <span class="summary-value">{formatCurrency(charityGroup.net_payment)}</span>
                        </div>
                        {/if}
                        {#if charityGroup.payment_reference}
                        <div class="summary-item">
                          <span class="summary-label">Payment Reference</span>
                          <span class="summary-value reference-value">{charityGroup.payment_reference}</span>
                        </div>
                        {/if}
                        {#if charityGroup.transaction_date}
                        <div class="summary-item">
                          <span class="summary-label">Transaction Date</span>
                          <span class="summary-value">{formatDate(charityGroup.transaction_date)}</span>
                        </div>
                        {/if}
                      </div>
                    </div>
                    {/if}
                    <h4 class="donators-title">Donations ({charityGroup.donators.length})</h4>
                    {#each charityGroup.donators as donator (donator.uuid)}
                      <div class="donator-item">
                        <div class="donator-info">
                          <span class="donator-name">{donator.name}</span>
                          <span class="donator-hours">{donator.hours} hours</span>
                        </div>
                        <div class="donator-amounts">
                          <span class="donator-total">{formatCurrency(donator.amount)}</span>
                          <span class="donator-contribution">Contribution: {formatCurrency(donator.employer_top_up || donator.contribution || 0)} <span class="included-note">(included)</span></span>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .paid-invoice-page {
    padding: 0;
    max-width: 100%;
  }

  .invoice-section {
    display: flex;
    flex-direction: column;
  }

  .invoice-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #f3f4f6;
  }

  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #e5e7eb;
    gap: 1rem;
  }

  .invoice-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
  }

  .invoice-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .invoice-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  /* "With L2G" badge — cyan to match the Completed Payments listing (canonical palette). */
  .paid-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    background-color: #cffafe;
    color: #155e75;
  }

  .invoice-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .separator {
    color: #d1d5db;
  }

  .total-amount {
    font-weight: 600;
    color: #111827;
    font-size: 1rem;
  }

  .donators-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .charity-group {
    background: #f9fafb;
    border-radius: 8px;
    padding: 0;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .charity-header-button {
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    z-index: 1;
    position: relative;
  }

  .charity-header-button:hover {
    background-color: #f3f4f6;
  }

  .charity-header-button:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    background-color: #f3f4f6;
  }

  .charity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    margin: 0;
    border-bottom: 1px solid #e5e7eb;
    pointer-events: none;
  }

  .charity-header-content {
    flex: 1;
    pointer-events: none;
  }

  .charity-name-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .charity-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .charity-status-badge.status-completed {
    background-color: #d1fae5;
    color: #065f46;
  }

  .charity-status-badge.status-pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .expand-icon {
    flex-shrink: 0;
    color: #6b7280;
    transition: transform 0.2s ease;
    margin-left: 1rem;
  }

  .expand-icon.expanded {
    transform: rotate(180deg);
  }

  .charity-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .charity-total {
    font-weight: 600;
    color: #111827;
  }

  .donators-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.25rem;
    padding-top: 0.75rem;
    animation: slideDown 0.2s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }

  .donator-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .donator-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .donator-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .donator-hours {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .donator-amounts {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }

  .donator-total {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .donator-contribution {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .charity-contribution {
    font-weight: 500;
    color: #374151;
  }

  .contribution-summary-top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
  }

  .contribution-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .contribution-value {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .contribution-note {
    font-size: 0.75rem;
    color: #6b7280;
    font-style: italic;
  }

  .included-note {
    font-size: 0.7em;
    color: #9ca3af;
    font-style: italic;
  }

  .l2g-fee {
    color: #f59e0b;
    font-weight: 500;
  }

  .net-payment {
    color: #059669;
    font-weight: 600;
  }

  .payment-ref {
    font-family: monospace;
    font-size: 0.8125rem;
    color: #6366f1;
    font-weight: 500;
  }

  .payment-summary-card {
    background: white;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid #e5e7eb;
  }

  .summary-title {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1rem 0;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .summary-label {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 500;
  }

  .summary-value {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 600;
  }

  .summary-value.reference-value {
    font-family: monospace;
    color: #6366f1;
  }

  .donators-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 1rem 0 0.75rem 0;
  }

  .contribution-summary {
    padding-top: 1.5rem;
    border-top: 2px solid #e5e7eb;
    margin-top: 1rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
  }

  .summary-label {
    font-size: 1rem;
    font-weight: 500;
    color: #374151;
  }

  .summary-value {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .error-message {
    text-align: center;
    padding: 3rem;
    color: #ef4444;
  }

  .error-message p {
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  .loading-message {
    text-align: center;
    padding: 3rem;
    color: #6b7280;
  }

  .page-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    background: transparent;
    border: none;
    color: var(--color-primary);
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;
  }

  .back-link:hover {
    color: var(--color-primary-hover);
  }

  .back-link svg {
    width: 1.2rem;
    height: 1.2rem;
  }

  .download-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: var(--color-primary);
    color: var(--color-text-dark);
    border: none;
    border-radius: var(--border-radius-button);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;
  }

  .download-button:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  .download-button:active:not(:disabled) {
    transform: translateY(1px);
  }

  .download-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .download-button svg {
    width: 1.2rem;
    height: 1.2rem;
    flex-shrink: 0;
  }

  .download-spinner {
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .invoice-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>

